import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Link,
  useSearchParams,
} from "react-router-dom";

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Spinner,
} from "react-bootstrap";

import axios from "axios";
import { useCart } from "../context/CartContext";
import jsPDF from "jspdf";

// ============================================================
// API CONFIGURATION
// ============================================================

const API_URL = (
  process.env.REACT_APP_API_URL ||
  "https://apimyshop.myidealtd.co.uk/api"
)
  .trim()
  .replace(/\/+$/, "");

const IMAGE_BASE_URL = API_URL
  .replace(/\/api\/?$/i, "")
  .replace(/\/+$/, "");

const DEFAULT_IMAGE = "/images/no-image.png";

// ============================================================
// DEBUG
// ============================================================

console.log("==========================================");
console.log("SUCCESS.JS CONFIGURATION");
console.log("API_URL:", API_URL);
console.log("IMAGE_BASE_URL:", IMAGE_BASE_URL);
console.log("==========================================");

// ============================================================
// MONGODB OBJECT ID CHECK
// ============================================================

const isMongoObjectId = (value) => {
  return (
    typeof value === "string" &&
    /^[a-fA-F0-9]{24}$/.test(
      value.trim()
    )
  );
};

// ============================================================
// EXTRACT IMAGE FROM ANY VALUE
// ============================================================

const extractFromImageCandidate = (
  candidate
) => {
  if (!candidate) {
    return "";
  }

  // ----------------------------------------------------------
  // STRING
  // ----------------------------------------------------------

  if (typeof candidate === "string") {
    return candidate.trim();
  }

  // ----------------------------------------------------------
  // ARRAY
  // ----------------------------------------------------------

  if (Array.isArray(candidate)) {
    for (const item of candidate) {
      const result =
        extractFromImageCandidate(
          item
        );

      if (result) {
        return result;
      }
    }

    return "";
  }

  // ----------------------------------------------------------
  // OBJECT
  // ----------------------------------------------------------

  if (
    typeof candidate === "object" &&
    candidate !== null
  ) {
    const possibleImages = [
      candidate.main,
      candidate.original,
      candidate.cart,
      candidate.thumbnail,
      candidate.url,
      candidate.src,
      candidate.path,
      candidate.filename,
      candidate.file,
      candidate.image,
      candidate.imageUrl,
      candidate.imageURL,
      candidate.photo,
      candidate.picture,
      candidate.value,
    ];

    for (
      const value of possibleImages
    ) {
      const result =
        extractFromImageCandidate(
          value
        );

      if (result) {
        return result;
      }
    }
  }

  return "";
};

// ============================================================
// EXTRACT IMAGE FROM PRODUCT / ORDER PRODUCT
// ============================================================

const extractImageValue = (item) => {
  if (!item) {
    return "";
  }

  // ----------------------------------------------------------
  // Direct image properties
  // ----------------------------------------------------------

  const directFields = [
    item.image,
    item.imageUrl,
    item.imageURL,
    item.photo,
    item.picture,
    item.thumbnail,
    item.mainImage,
    item.productImage,
  ];

  for (
    const candidate of directFields
  ) {
    const image =
      extractFromImageCandidate(
        candidate
      );

    if (image) {
      return image;
    }
  }

  // ----------------------------------------------------------
  // images
  // ----------------------------------------------------------

  const images =
    extractFromImageCandidate(
      item.images
    );

  if (images) {
    return images;
  }

  // ----------------------------------------------------------
  // Nested product
  // ----------------------------------------------------------

  if (
    item.product &&
    typeof item.product === "object"
  ) {
    const image =
      extractImageValue(
        item.product
      );

    if (image) {
      return image;
    }
  }

  // ----------------------------------------------------------
  // Nested productId
  // ----------------------------------------------------------

  if (
    item.productId &&
    typeof item.productId === "object"
  ) {
    const image =
      extractImageValue(
        item.productId
      );

    if (image) {
      return image;
    }
  }

  // ----------------------------------------------------------
  // Nested id
  // ----------------------------------------------------------

  if (
    item.id &&
    typeof item.id === "object"
  ) {
    const image =
      extractImageValue(
        item.id
      );

    if (image) {
      return image;
    }
  }

  return "";
};

// ============================================================
// CLEAN IMAGE PATH
// ============================================================

const cleanImagePath = (value) => {
  if (
    typeof value !== "string" ||
    !value.trim()
  ) {
    return "";
  }

  let image =
    value.trim();

  // Remove leading slash
  image =
    image.replace(/^\/+/, "");

  // Remove api prefix
  image =
    image.replace(
      /^api\/+/i,
      ""
    );

  return image;
};

// ============================================================
// BUILD IMAGE URL
// ============================================================

const getImageCandidates = (
  item
) => {
  const rawImage =
    extractImageValue(item);

  console.log(
    "SUCCESS IMAGE RAW VALUE:",
    rawImage
  );

  if (!rawImage) {
    return [];
  }

  const image =
    String(rawImage).trim();

  // ----------------------------------------------------------
  // Absolute URL
  // ----------------------------------------------------------

  if (
    image.startsWith(
      "http://"
    ) ||
    image.startsWith(
      "https://"
    )
  ) {
    return [image];
  }

  // ----------------------------------------------------------
  // Data URL
  // ----------------------------------------------------------

  if (
    image.startsWith("data:")
  ) {
    return [image];
  }

  // ----------------------------------------------------------
  // Blob URL
  // ----------------------------------------------------------

  if (
    image.startsWith("blob:")
  ) {
    return [image];
  }

  const cleaned =
    cleanImagePath(image);

  if (!cleaned) {
    return [];
  }

  const lower =
    cleaned.toLowerCase();

  const candidates = [];

  // ----------------------------------------------------------
  // uploads/...
  // ----------------------------------------------------------

  if (
    lower.startsWith(
      "uploads/"
    )
  ) {
    candidates.push(
      `${IMAGE_BASE_URL}/${cleaned}`
    );

    return [
      ...new Set(candidates),
    ];
  }

  // ----------------------------------------------------------
  // products/...
  // ----------------------------------------------------------

  if (
    lower.startsWith(
      "products/"
    )
  ) {
    candidates.push(
      `${IMAGE_BASE_URL}/uploads/${cleaned}`
    );

    candidates.push(
      `${IMAGE_BASE_URL}/${cleaned}`
    );

    return [
      ...new Set(candidates),
    ];
  }

  // ----------------------------------------------------------
  // images/...
  // ----------------------------------------------------------

  if (
    lower.startsWith(
      "images/"
    )
  ) {
    candidates.push(
      `${IMAGE_BASE_URL}/${cleaned}`
    );

    candidates.push(
      `${IMAGE_BASE_URL}/uploads/${cleaned}`
    );

    return [
      ...new Set(candidates),
    ];
  }

  // ----------------------------------------------------------
  // static/...
  // ----------------------------------------------------------

  if (
    lower.startsWith(
      "static/"
    )
  ) {
    candidates.push(
      `${IMAGE_BASE_URL}/${cleaned}`
    );

    candidates.push(
      `${IMAGE_BASE_URL}/uploads/${cleaned}`
    );

    return [
      ...new Set(candidates),
    ];
  }

  // ----------------------------------------------------------
  // media/...
  // ----------------------------------------------------------

  if (
    lower.startsWith(
      "media/"
    )
  ) {
    candidates.push(
      `${IMAGE_BASE_URL}/${cleaned}`
    );

    candidates.push(
      `${IMAGE_BASE_URL}/uploads/${cleaned}`
    );

    return [
      ...new Set(candidates),
    ];
  }

  // ----------------------------------------------------------
  // Filename only
  // ----------------------------------------------------------

  candidates.push(
    `${IMAGE_BASE_URL}/uploads/${cleaned}`
  );

  candidates.push(
    `${IMAGE_BASE_URL}/${cleaned}`
  );

  candidates.push(
    `${IMAGE_BASE_URL}/uploads/products/${cleaned}`
  );

  candidates.push(
    `${IMAGE_BASE_URL}/products/${cleaned}`
  );

  return [
    ...new Set(candidates),
  ];
};

// ============================================================
// FIRST IMAGE URL
// ============================================================

const getImageUrl = (
  item
) => {
  const candidates =
    getImageCandidates(item);

  if (!candidates.length) {
    return DEFAULT_IMAGE;
  }

  return candidates[0];
};

// ============================================================
// GET PRODUCT ID FROM ORDER PRODUCT
// ============================================================

const getProductId = (
  product
) => {
  if (!product) {
    return "";
  }

  // ----------------------------------------------------------
  // IMPORTANT:
  // Your screenshot shows product.image contains
  // the MongoDB Product ID.
  // ----------------------------------------------------------

  if (
    isMongoObjectId(
      product.image
    )
  ) {
    return product.image;
  }

  // ----------------------------------------------------------
  // product.id
  // ----------------------------------------------------------

  if (
    typeof product.id === "string" &&
    isMongoObjectId(
      product.id
    )
  ) {
    return product.id;
  }

  // ----------------------------------------------------------
  // product.productId
  // ----------------------------------------------------------

  if (
    typeof product.productId === "string" &&
    isMongoObjectId(
      product.productId
    )
  ) {
    return product.productId;
  }

  // ----------------------------------------------------------
  // populated product.id
  // ----------------------------------------------------------

  if (
    product.id &&
    typeof product.id === "object" &&
    product.id._id
  ) {
    return String(
      product.id._id
    );
  }

  // ----------------------------------------------------------
  // populated productId
  // ----------------------------------------------------------

  if (
    product.productId &&
    typeof product.productId === "object" &&
    product.productId._id
  ) {
    return String(
      product.productId._id
    );
  }

  return "";
};

// ============================================================
// FETCH REAL PRODUCT
// ============================================================

const fetchProductById = async (
  productId
) => {
  if (
    !isMongoObjectId(
      productId
    )
  ) {
    return null;
  }

  try {
    console.log(
      "=========================================="
    );

    console.log(
      "🔎 FETCHING PRODUCT FOR SUCCESS IMAGE"
    );

    console.log(
      "Product ID:",
      productId
    );

    console.log(
      "URL:",
      `${API_URL}/products/${productId}`
    );

    const response =
      await axios.get(
        `${API_URL}/products/${encodeURIComponent(
          productId
        )}`,
        {
          withCredentials: true,
        }
      );

    console.log(
      "✅ PRODUCT API RESPONSE:",
      response.data
    );

    // --------------------------------------------------------
    // Support different response formats
    // --------------------------------------------------------

    const data =
      response?.data;

    if (!data) {
      return null;
    }

    if (
      data.product &&
      typeof data.product ===
        "object"
    ) {
      return data.product;
    }

    if (
      data.data &&
      typeof data.data ===
        "object"
    ) {
      return data.data;
    }

    return data;
  } catch (error) {
    console.error(
      "❌ PRODUCT FETCH FAILED"
    );

    console.error(
      "Product ID:",
      productId
    );

    console.error(
      "Status:",
      error?.response?.status
    );

    console.error(
      "Response:",
      error?.response?.data
    );

    console.error(
      "Error:",
      error
    );

    return null;
  }
};

// ============================================================
// FORMAT MONEY
// ============================================================

const formatMoney = (
  value
) => {
  const amount =
    Number(value) || 0;

  return new Intl.NumberFormat(
    "en-GB",
    {
      style: "currency",
      currency: "GBP",
    }
  ).format(amount);
};

// ============================================================
// FORMAT DATE
// ============================================================

const formatDate = (
  value
) => {
  if (!value) {
    return "";
  }

  try {
    return new Date(
      value
    ).toLocaleString(
      "en-GB",
      {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }
    );
  } catch (error) {
    return String(value);
  }
};

// ============================================================
// SUCCESS COMPONENT
// ============================================================

function Success() {
  const [
    searchParams,
  ] = useSearchParams();

  const {
    resetCart,
  } = useCart();

  // ==========================================================
  // REFS
  // ==========================================================

  const loadedSessionRef =
    useRef(null);

  const cartResetRef =
    useRef(false);

  // ==========================================================
  // STATE
  // ==========================================================

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  const [
    order,
    setOrder,
  ] = useState(null);

  const [
    sessionData,
    setSessionData,
  ] = useState(null);

  const [
    productImages,
    setProductImages,
  ] = useState({});

  const [
    invoiceLoading,
    setInvoiceLoading,
  ] = useState(false);

  // ==========================================================
  // SESSION ID
  // ==========================================================

  const sessionId =
    searchParams.get(
      "session_id"
    );

  // ==========================================================
  // LOAD SUCCESS ORDER
  // ==========================================================

  useEffect(() => {
    let mounted = true;

    const loadOrder =
      async () => {
        try {
          setLoading(true);
          setError("");

          // --------------------------------------------------
          // SESSION CHECK
          // --------------------------------------------------

          if (!sessionId) {
            throw new Error(
              "Payment session ID is missing."
            );
          }

          // --------------------------------------------------
          // PREVENT DUPLICATE SESSION LOAD
          // --------------------------------------------------

          if (
            loadedSessionRef.current ===
            sessionId
          ) {
            return;
          }

          loadedSessionRef.current =
            sessionId;

          console.log("");
          console.log(
            "=========================================="
          );

          console.log(
            "💳 SUCCESS PAGE STARTED"
          );

          console.log(
            "Session ID:",
            sessionId
          );

          console.log(
            "API URL:",
            API_URL
          );

          console.log(
            "IMAGE BASE URL:",
            IMAGE_BASE_URL
          );

          console.log(
            "=========================================="
          );

          // ==================================================
          // GET STRIPE SESSION
          // ==================================================

          console.log(
            "🔎 Getting Stripe session..."
          );

          const sessionResponse =
            await axios.get(
              `${API_URL}/stripe/session/${encodeURIComponent(
                sessionId
              )}`,
              {
                withCredentials: true,
              }
            );

          if (!mounted) {
            return;
          }

          const loadedSession =
            sessionResponse.data;

          console.log(
            "✅ STRIPE SESSION:",
            loadedSession
          );

          setSessionData(
            loadedSession
          );

          // ==================================================
          // FIND ORDER ID
          // ==================================================

          const orderId =
            loadedSession?.orderId ||
            loadedSession?.metadata?.orderId ||
            loadedSession?.metadata?.order_id ||
            loadedSession?.order?.orderId ||
            loadedSession?.order?._id;

          console.log(
            "🆔 ORDER ID:",
            orderId
          );

          if (!orderId) {
            throw new Error(
              "Order ID could not be found."
            );
          }

          // ==================================================
          // GET ORDER
          // ==================================================

          console.log(
            "🔎 Getting order..."
          );

          const orderResponse =
            await axios.get(
              `${API_URL}/orders/success/${encodeURIComponent(
                orderId
              )}`,
              {
                withCredentials: true,
              }
            );

          if (!mounted) {
            return;
          }

          const loadedOrder =
            orderResponse.data;

          console.log("");
          console.log(
            "=========================================="
          );

          console.log(
            "✅ COMPLETE SUCCESS ORDER RESPONSE"
          );

          console.log(
            loadedOrder
          );

          console.log(
            "=========================================="
          );

          console.log(
            "🛒 ORDER PRODUCTS:",
            loadedOrder?.products
          );

          // ==================================================
          // FIND REAL PRODUCT IMAGES
          // ==================================================

          const resolvedImages = {};

          if (
            Array.isArray(
              loadedOrder?.products
            )
          ) {
            for (
              const product of
                loadedOrder.products
            ) {
              if (!mounted) {
                return;
              }

              // ----------------------------------------------
              // First check if normal image exists
              // ----------------------------------------------

              const normalImage =
                extractImageValue(
                  product
                );

              // ----------------------------------------------
              // If image is MongoDB ID,
              // fetch the real product.
              // ----------------------------------------------

              const productId =
                getProductId(
                  product
                );

              console.log("");
              console.log(
                "------------------------------------------"
              );

              console.log(
                "🖼️ PROCESSING PRODUCT:",
                product?.name
              );

              console.log(
                "Order image:",
                product?.image
              );

              console.log(
                "Order product ID:",
                product?.id
              );

              console.log(
                "Detected Product ID:",
                productId
              );

              console.log(
                "Normal image:",
                normalImage
              );

              // ----------------------------------------------
              // IMPORTANT:
              // If product.image is the Mongo ID,
              // DO NOT use it as an image filename.
              // ----------------------------------------------

              if (
                isMongoObjectId(
                  product?.image
                )
              ) {
                console.log(
                  "⚠️ product.image is a MongoDB Product ID."
                );

                const realProduct =
                  await fetchProductById(
                    product.image
                  );

                if (
                  realProduct &&
                  mounted
                ) {
                  console.log(
                    "✅ REAL PRODUCT:",
                    realProduct
                  );

                  const realImage =
                    extractImageValue(
                      realProduct
                    );

                  console.log(
                    "🖼️ REAL IMAGE FROM PRODUCT:",
                    realImage
                  );

                  if (realImage) {
                    const imageUrl =
                      getImageUrl({
                        image:
                          realImage,
                      });

                    resolvedImages[
                      product.image
                    ] = imageUrl;

                    console.log(
                      "✅ RESOLVED PRODUCT IMAGE:",
                      imageUrl
                    );
                  }
                }

                continue;
              }

              // ----------------------------------------------
              // If product ID exists separately
              // ----------------------------------------------

              if (
                productId &&
                !normalImage
              ) {
                const realProduct =
                  await fetchProductById(
                    productId
                  );

                if (
                  realProduct &&
                  mounted
                ) {
                  const realImage =
                    extractImageValue(
                      realProduct
                    );

                  if (realImage) {
                    const imageUrl =
                      getImageUrl({
                        image:
                          realImage,
                      });

                    resolvedImages[
                      productId
                    ] = imageUrl;

                    console.log(
                      "✅ RESOLVED IMAGE:",
                      imageUrl
                    );
                  }
                }
              }

              console.log(
                "------------------------------------------"
              );
            }
          }

          // ==================================================
          // SAVE RESOLVED IMAGES
          // ==================================================

          if (mounted) {
            console.log(
              "=========================================="
            );

            console.log(
              "🖼️ ALL RESOLVED PRODUCT IMAGES:",
              resolvedImages
            );

            console.log(
              "=========================================="
            );

            setProductImages(
              resolvedImages
            );
          }

          // ==================================================
          // SET ORDER
          // ==================================================

          if (mounted) {
            setOrder(
              loadedOrder
            );
          }

          // ==================================================
          // CLEAR CART
          // ==================================================

          if (
            mounted &&
            !cartResetRef.current &&
            typeof resetCart ===
              "function"
          ) {
            try {
              resetCart();

              cartResetRef.current =
                true;

              console.log(
                "🛒 Cart cleared successfully."
              );
            } catch (
              cartError
            ) {
              console.warn(
                "Could not clear cart:",
                cartError
              );
            }
          }
        } catch (err) {
          console.error(
            "❌ SUCCESS PAGE ERROR:",
            err
          );

          if (!mounted) {
            return;
          }

          const message =
            err?.response?.data
              ?.message ||
            err?.message ||
            "Unable to load your order.";

          setError(
            message
          );
        } finally {
          if (mounted) {
            setLoading(
              false
            );
          }
        }
      };

    loadOrder();

    return () => {
      mounted = false;
    };
  }, [sessionId]);

  // ==========================================================
  // PRODUCTS
  // ==========================================================

  const products =
    useMemo(() => {
      if (
        !Array.isArray(
          order?.products
        )
      ) {
        return [];
      }

      return order.products;
    }, [order]);

  // ==========================================================
  // TOTALS
  // ==========================================================

  const subtotal =
    Number(
      order?.subtotal
    ) || 0;

  const shipping =
    Number(
      order?.shipping
    ) || 0;

  const tax =
    Number(
      order?.tax
    ) || 0;

  const total =
    Number(
      order?.total
    ) ||
    subtotal +
      shipping +
      tax;

  // ==========================================================
  // CUSTOMER
  // ==========================================================

  const customer =
    order?.userId || {};

  const customerName =
    customer?.name ||
    sessionData?.customer_name ||
    order?.shippingAddress?.name ||
    "Customer";

  const customerEmail =
    customer?.email ||
    sessionData?.email ||
    "";

  // ==========================================================
  // SHIPPING ADDRESS
  // ==========================================================

  const shippingAddress =
    order?.shippingAddress || {};

  // ==========================================================
  // PAYMENT STATUS
  // ==========================================================

  const paymentStatus =
    order?.paymentStatus ||
    "paid";

  // ==========================================================
  // ORDER NUMBER
  // ==========================================================

  const orderNumber =
    order?._id
      ? String(order._id)
      : "";

  // ==========================================================
  // GET DISPLAY IMAGE FOR PRODUCT
  // ==========================================================

  const getDisplayImage = (
    product
  ) => {
    if (!product) {
      return DEFAULT_IMAGE;
    }

    // --------------------------------------------------------
    // IMPORTANT:
    // If image is a MongoDB Product ID,
    // use the image fetched from product API.
    // --------------------------------------------------------

    if (
      isMongoObjectId(
        product.image
      )
    ) {
      const fetchedImage =
        productImages[
          product.image
        ];

      if (fetchedImage) {
        return fetchedImage;
      }

      return DEFAULT_IMAGE;
    }

    // --------------------------------------------------------
    // If id is Product ID and image doesn't exist
    // --------------------------------------------------------

    const productId =
      getProductId(
        product
      );

    if (
      productId &&
      productImages[
        productId
      ]
    ) {
      return productImages[
        productId
      ];
    }

    // --------------------------------------------------------
    // Normal image
    // --------------------------------------------------------

    const candidates =
      getImageCandidates(
        product
      );

    if (
      candidates.length
    ) {
      return candidates[0];
    }

    return DEFAULT_IMAGE;
  };

  // ==========================================================
  // IMAGE ERROR
  // ==========================================================

  const handleImageError = (
    event,
    product
  ) => {
    const img =
      event.currentTarget;

    const failedUrl =
      img?.src || "";

    console.error("");
    console.error(
      "=========================================="
    );

    console.error(
      "❌ SUCCESS IMAGE FAILED"
    );

    console.error(
      "Product:",
      product?.name
    );

    console.error(
      "Product image:",
      product?.image
    );

    console.error(
      "Product id:",
      product?.id
    );

    console.error(
      "Failed URL:",
      failedUrl
    );

    console.error(
      "=========================================="
    );

    // Prevent infinite loop
    img.onerror = null;

    img.src =
      DEFAULT_IMAGE;
  };

  // ==========================================================
  // DOWNLOAD INVOICE
  // ==========================================================

  const downloadInvoice = () => {
    if (!order) {
      return;
    }

    try {
      setInvoiceLoading(
        true
      );

      const doc =
        new jsPDF();

      // ------------------------------------------------------
      // HEADER
      // ------------------------------------------------------

      doc.setFontSize(
        20
      );

      doc.text(
        "ORDER INVOICE",
        20,
        25
      );

      doc.setFontSize(
        10
      );

      doc.text(
        `Order ID: ${orderNumber}`,
        20,
        35
      );

      doc.text(
        `Date: ${formatDate(
          order.createdAt
        )}`,
        20,
        42
      );

      // ------------------------------------------------------
      // CUSTOMER
      // ------------------------------------------------------

      doc.setFontSize(
        13
      );

      doc.text(
        "Customer",
        20,
        58
      );

      doc.setFontSize(
        10
      );

      doc.text(
        `Name: ${customerName}`,
        20,
        67
      );

      if (
        customerEmail
      ) {
        doc.text(
          `Email: ${customerEmail}`,
          20,
          74
        );
      }

      // ------------------------------------------------------
      // SHIPPING
      // ------------------------------------------------------

      let shippingY =
        88;

      doc.setFontSize(
        13
      );

      doc.text(
        "Shipping Address",
        20,
        shippingY
      );

      doc.setFontSize(
        10
      );

      shippingY += 8;

      if (
        shippingAddress.address
      ) {
        doc.text(
          shippingAddress.address,
          20,
          shippingY
        );

        shippingY += 6;
      }

      if (
        shippingAddress.city
      ) {
        doc.text(
          shippingAddress.city,
          20,
          shippingY
        );

        shippingY += 6;
      }

      if (
        shippingAddress.postcode
      ) {
        doc.text(
          shippingAddress.postcode,
          20,
          shippingY
        );

        shippingY += 6;
      }

      if (
        shippingAddress.country
      ) {
        doc.text(
          shippingAddress.country,
          20,
          shippingY
        );
      }

      // ------------------------------------------------------
      // PRODUCTS
      // ------------------------------------------------------

      let y =
        shippingY + 18;

      doc.setFontSize(
        13
      );

      doc.text(
        "Products",
        20,
        y
      );

      y += 10;

      doc.setFontSize(
        10
      );

      products.forEach(
        (
          product,
          index
        ) => {
          const quantity =
            Number(
              product?.quantity
            ) || 1;

          const price =
            Number(
              product?.price
            ) || 0;

          const lineTotal =
            quantity * price;

          doc.text(
            `${index + 1}. ${
              product?.name ||
              "Product"
            }`,
            20,
            y
          );

          doc.text(
            `${quantity} x ${formatMoney(
              price
            )}`,
            120,
            y
          );

          doc.text(
            formatMoney(
              lineTotal
            ),
            165,
            y
          );

          y += 8;

          if (
            y > 260
          ) {
            doc.addPage();
            y = 20;
          }
        }
      );

      // ------------------------------------------------------
      // TOTALS
      // ------------------------------------------------------

      y += 8;

      doc.text(
        `Subtotal: ${formatMoney(
          subtotal
        )}`,
        130,
        y
      );

      y += 7;

      doc.text(
        `Shipping: ${formatMoney(
          shipping
        )}`,
        130,
        y
      );

      if (
        tax > 0
      ) {
        y += 7;

        doc.text(
          `Tax: ${formatMoney(
            tax
          )}`,
          130,
          y
        );
      }

      y += 10;

      doc.setFontSize(
        12
      );

      doc.text(
        `Total: ${formatMoney(
          total
        )}`,
        130,
        y
      );

      y += 20;

      doc.setFontSize(
        10
      );

      doc.text(
        "Thank you for your order.",
        20,
        y
      );

      doc.save(
        `invoice-${
          orderNumber ||
          "order"
        }.pdf`
      );
    } catch (err) {
      console.error(
        "❌ Invoice generation failed:",
        err
      );
    } finally {
      setInvoiceLoading(
        false
      );
    }
  };

  // ==========================================================
  // WHATSAPP
  // ==========================================================

  const openWhatsApp = () => {
    if (!order) {
      return;
    }

    const message = [
      "Hello, I have completed an order.",
      "",
      `Order ID: ${orderNumber}`,
      `Total: ${formatMoney(
        total
      )}`,
      `Name: ${customerName}`,
    ]
      .filter(Boolean)
      .join("\n");

    const url =
      `https://wa.me/?text=${encodeURIComponent(
        message
      )}`;

    window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loading) {
    return (
      <Container className="py-5">
        <div className="text-center py-5">
          <Spinner
            animation="border"
            role="status"
          />

          <div className="mt-3">
            Loading your order...
          </div>
        </div>
      </Container>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (error) {
    return (
      <Container className="py-5">
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-4 p-md-5 text-center">
            <Alert variant="danger">
              <h4>
                Unable to load your order
              </h4>

              <p className="mb-0">
                {error}
              </p>
            </Alert>

            <Link
              to="/"
              className="btn btn-primary"
            >
              Continue Shopping
            </Link>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // ==========================================================
  // ORDER NOT FOUND
  // ==========================================================

  if (!order) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          We could not find your order.
        </Alert>

        <Link
          to="/"
          className="btn btn-primary"
        >
          Continue Shopping
        </Link>
      </Container>
    );
  }

  // ==========================================================
  // MAIN PAGE
  // ==========================================================

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col
          xs={12}
          lg={10}
          xl={9}
        >
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-3 p-md-5">

              {/* =================================================
                  SUCCESS HEADER
              ================================================= */}

              <div className="text-center mb-4">

                <div
                  style={{
                    width: "72px",
                    height: "72px",
                    borderRadius: "50%",
                    backgroundColor:
                      "#198754",
                    color: "#fff",
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "center",
                    fontSize:
                      "38px",
                    fontWeight:
                      "bold",
                    margin:
                      "0 auto 20px",
                  }}
                >
                  ✓
                </div>

                <h1 className="fw-bold">
                  Payment Successful
                </h1>

                <p className="text-muted">
                  Thank you for your order{" "}
                  <strong>
                    {customerName}
                  </strong>
                  .
                </p>

                <Badge
                  bg="success"
                  className="px-3 py-2"
                >
                  Payment{" "}
                  {paymentStatus}
                </Badge>

              </div>

              {/* =================================================
                  ORDER DETAILS
              ================================================= */}

              <Card className="border mb-4">
                <Card.Body>

                  <Row>

                    <Col
                      xs={12}
                      md={6}
                      className="mb-3 mb-md-0"
                    >
                      <div className="text-muted small">
                        Order ID
                      </div>

                      <div
                        className="fw-semibold"
                        style={{
                          wordBreak:
                            "break-all",
                        }}
                      >
                        {orderNumber}
                      </div>
                    </Col>

                    <Col
                      xs={12}
                      md={6}
                    >
                      <div className="text-muted small">
                        Order Date
                      </div>

                      <div className="fw-semibold">
                        {formatDate(
                          order.createdAt
                        )}
                      </div>
                    </Col>

                  </Row>

                </Card.Body>
              </Card>

              {/* =================================================
                  CUSTOMER + SHIPPING
              ================================================= */}

              <Row className="mb-4">

                <Col
                  xs={12}
                  md={6}
                  className="mb-3 mb-md-0"
                >
                  <Card className="border h-100">
                    <Card.Body>

                      <h5 className="fw-bold mb-3">
                        Customer Details
                      </h5>

                      <div className="mb-2">
                        <strong>
                          Name:
                        </strong>{" "}
                        {customerName}
                      </div>

                      {customerEmail && (
                        <div className="mb-2">
                          <strong>
                            Email:
                          </strong>{" "}
                          {customerEmail}
                        </div>
                      )}

                      {customer?.phone && (
                        <div>
                          <strong>
                            Phone:
                          </strong>{" "}
                          {customer.phone}
                        </div>
                      )}

                    </Card.Body>
                  </Card>
                </Col>

                <Col
                  xs={12}
                  md={6}
                >
                  <Card className="border h-100">
                    <Card.Body>

                      <h5 className="fw-bold mb-3">
                        Shipping Address
                      </h5>

                      {shippingAddress.address && (
                        <div>
                          {
                            shippingAddress.address
                          }
                        </div>
                      )}

                      {shippingAddress.city && (
                        <div>
                          {
                            shippingAddress.city
                          }
                        </div>
                      )}

                      {shippingAddress.postcode && (
                        <div>
                          {
                            shippingAddress.postcode
                          }
                        </div>
                      )}

                      {shippingAddress.country && (
                        <div>
                          {
                            shippingAddress.country
                          }
                        </div>
                      )}

                    </Card.Body>
                  </Card>
                </Col>

              </Row>

              {/* =================================================
                  PRODUCTS
              ================================================= */}

              <Card className="border mb-4">
                <Card.Body>

                  <h5 className="fw-bold mb-4">
                    Your Products
                  </h5>

                  {products.length === 0 ? (
                    <Alert variant="warning">
                      No products were found
                      in this order.
                    </Alert>
                  ) : (
                    products.map(
                      (
                        product,
                        index
                      ) => {

                        const imageUrl =
                          getDisplayImage(
                            product
                          );

                        const quantity =
                          Number(
                            product?.quantity
                          ) || 1;

                        const price =
                          Number(
                            product?.price
                          ) || 0;

                        const lineTotal =
                          quantity *
                          price;

                        console.log(
                          "=========================================="
                        );

                        console.log(
                          "🖼️ RENDERING SUCCESS IMAGE"
                        );

                        console.log(
                          "Product:",
                          product?.name
                        );

                        console.log(
                          "Order image:",
                          product?.image
                        );

                        console.log(
                          "Resolved image:",
                          imageUrl
                        );

                        console.log(
                          "=========================================="
                        );

                        return (
                          <div
                            key={
                              product?._id ||
                              product?.id ||
                              product?.productId ||
                              index
                            }
                            className="py-3 border-bottom"
                          >

                            <Row className="align-items-center">

                              {/* =================================
                                  IMAGE
                              ================================= */}

                              <Col
                                xs={4}
                                sm={3}
                                md={2}
                              >

                                <div
                                  style={{
                                    width:
                                      "100%",
                                    height:
                                      "120px",
                                    backgroundColor:
                                      "#f8f9fa",
                                    border:
                                      "1px solid #dee2e6",
                                    borderRadius:
                                      "8px",
                                    display:
                                      "flex",
                                    alignItems:
                                      "center",
                                    justifyContent:
                                      "center",
                                    overflow:
                                      "hidden",
                                  }}
                                >

                                  <img
                                    src={
                                      imageUrl
                                    }
                                    alt={
                                      product?.name ||
                                      "Product"
                                    }
                                    loading="eager"
                                    onError={(
                                      event
                                    ) =>
                                      handleImageError(
                                        event,
                                        product
                                      )
                                    }
                                    style={{
                                      width:
                                        "100%",
                                      height:
                                        "100%",
                                      objectFit:
                                        "contain",
                                      display:
                                        "block",
                                    }}
                                  />

                                </div>

                              </Col>

                              {/* =================================
                                  PRODUCT INFORMATION
                              ================================= */}

                              <Col
                                xs={8}
                                sm={6}
                                md={6}
                              >

                                <h6 className="fw-bold mb-1">
                                  {
                                    product?.name ||
                                    "Product"
                                  }
                                </h6>

                                {product?.description && (
                                  <div className="text-muted small mb-2">
                                    {
                                      product.description
                                    }
                                  </div>
                                )}

                                <div className="text-muted small">
                                  Quantity:{" "}
                                  {
                                    quantity
                                  }
                                </div>

                                <div className="text-muted small">
                                  Price:{" "}
                                  {formatMoney(
                                    price
                                  )}
                                </div>

                              </Col>

                              {/* =================================
                                  LINE TOTAL
                              ================================= */}

                              <Col
                                xs={12}
                                sm={3}
                                md={4}
                                className="text-sm-end mt-2 mt-sm-0"
                              >

                                <strong>
                                  {formatMoney(
                                    lineTotal
                                  )}
                                </strong>

                              </Col>

                            </Row>

                          </div>
                        );
                      }
                    )
                  )}

                </Card.Body>
              </Card>

              {/* =================================================
                  TOTALS
              ================================================= */}

              <Row className="justify-content-end mb-4">

                <Col
                  xs={12}
                  md={6}
                  lg={5}
                >

                  <Card className="border">
                    <Card.Body>

                      <div className="d-flex justify-content-between mb-2">
                        <span>
                          Subtotal
                        </span>

                        <strong>
                          {formatMoney(
                            subtotal
                          )}
                        </strong>
                      </div>

                      <div className="d-flex justify-content-between mb-2">
                        <span>
                          Shipping
                        </span>

                        <strong>
                          {formatMoney(
                            shipping
                          )}
                        </strong>
                      </div>

                      {tax > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <span>
                            Tax
                          </span>

                          <strong>
                            {formatMoney(
                              tax
                            )}
                          </strong>
                        </div>
                      )}

                      <hr />

                      <div className="d-flex justify-content-between">

                        <strong>
                          Total
                        </strong>

                        <strong className="fs-5">
                          {formatMoney(
                            total
                          )}
                        </strong>

                      </div>

                    </Card.Body>
                  </Card>

                </Col>

              </Row>

              {/* =================================================
                  BUTTONS
              ================================================= */}

              <div className="d-flex flex-column flex-sm-row justify-content-center gap-2">

                <Button
                  variant="dark"
                  onClick={
                    downloadInvoice
                  }
                  disabled={
                    invoiceLoading
                  }
                >
                  {invoiceLoading
                    ? "Generating..."
                    : "Download Invoice"}
                </Button>

                <Button
                  variant="success"
                  onClick={
                    openWhatsApp
                  }
                >
                  Share Order
                </Button>

                <Link
                  to="/"
                  className="btn btn-primary"
                >
                  Continue Shopping
                </Link>

              </div>

              {/* =================================================
                  FOOTER
              ================================================= */}

              <div className="text-center text-muted small mt-4">
                Thank you for shopping with us.
              </div>

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Success;